import { useEffect, useState } from "react";
import axios from "axios";
import PublicContactSection from "../components/PublicContactSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = `${API}/member-payments/public/pricing/sheet.pdf`;

const FREQ_LABEL = { "1x": "1× / sem.", "2x": "2× / sem.", "3x": "3× / sem." };

function money(x) {
  if (x == null || x === "") return "—";
  const n = Number(x);
  if (!n) return "—";
  return `${n.toFixed(2).replace(".", ",")} $`;
}

export default function TarifsPublic() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [reco, setReco] = useState([]);

  useEffect(() => {
    document.title = "Tarifs — Institut Impact Élite Martial";
    axios.get(`${API}/member-payments/public/pricing`)
      .then((r) => setCfg(r.data))
      .catch(() => setErr("Impossible de charger les tarifs pour le moment."));
    axios.get(`${API}/public/recommended-tariffs`).then((r) => setReco(r.data.tariffs || [])).catch(() => {});
  }, []);

  const cycleLabel = (c) => ({ session: "/4 sem.", "4_semaines": "/4 sem.", mensuel: "/mois",
    trimestriel: "/3 mois", annuel: "/an", ponctuel: "", horaire: "/h", versements: "/mois" }[c] || "");

  if (err) return <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center p-6" data-testid="tarifs-error">{err}</div>;
  if (!cfg) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center p-6">Chargement des tarifs…</div>;

  const freqs = cfg.frequencies || ["1x", "2x", "3x"];
  const durations = cfg.durations || [];
  const surcharge = Number(cfg.surcharge_pct) || 0;
  const mult = 1 + surcharge / 100;
  const minW = cfg.installment_min_weeks || 12;
  const cell = (dur, f) => (cfg.grid?.[dur] || cfg.grid?.[String(dur)] || {})[f];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="tarifs-public">
      {/* Header co-brandé */}
      <div className="border-b border-amber-500/20 bg-gradient-to-b from-black to-zinc-950">
        <div className="max-w-4xl mx-auto px-5 py-10 text-center">
          <div className="text-[10px] tracking-[0.4em] text-amber-400/80 mb-3">INSTITUT IMPACT ÉLITE MARTIAL · KAIZEN SYSTEM</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white">Nos tarifs</h1>
          <p className="text-zinc-400 mt-3 text-sm max-w-2xl mx-auto">
            Programme <b className="text-amber-300">{cfg.program_name}</b> · Val-d'Or, QC.
            Choisis le <b>comptant</b> (réglé d'avance) ou le <b>prélèvement échelonné</b> (+{surcharge.toFixed(0)} %, dès {minW} semaines).
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a href="/cours-essai" data-testid="tarifs-cta-essai"
              className="px-6 py-3 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold text-sm transition">
              🥋 Réserver un cours d'essai — 19,99 $
            </a>
            <a href={PDF_URL} target="_blank" rel="noreferrer" data-testid="tarifs-pdf-link"
              className="px-6 py-3 rounded-full bg-zinc-800 hover:bg-zinc-700 text-amber-300 border border-amber-500/30 font-semibold text-sm transition">
              📄 Télécharger le PDF
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-10 space-y-10">
        {/* ⭐ Forfaits recommandés (Tarifs Dojo → Stripe) */}
        {reco.length > 0 && (
          <section data-testid="reco-section">
            <h2 className="text-lg font-bold text-amber-300 mb-1">⭐ Nos forfaits recommandés</h2>
            <p className="text-xs text-zinc-500 mb-4">Paiement sécurisé en ligne · taxes (TPS/TVQ) en sus.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reco-grid">
              {reco.map((t) => (
                <div key={t.id} data-testid={`reco-${t.id}`} className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-5 flex flex-col">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.age_range || t.program_id}</div>
                  <div className="text-white font-bold mt-1 leading-tight">{t.label}</div>
                  <div className="mt-auto pt-4">
                    <span className="text-2xl font-extrabold text-amber-300">{Number(t.price).toLocaleString("fr-CA")} $</span>
                    <span className="text-zinc-500 text-xs"> {cycleLabel(t.billing_cycle)}</span>
                    {t.stripe_payment_url ? (
                      <a href={t.stripe_payment_url} target="_blank" rel="noreferrer" data-testid={`reco-pay-${t.id}`}
                        className="block w-full mt-3 text-center bg-amber-400 text-black py-2.5 rounded-lg font-bold hover:bg-white transition">
                        {t.stripe_mode === "subscription" ? "S'abonner" : "Payer"}
                      </a>
                    ) : <div className="mt-3 text-center text-xs text-zinc-600 border border-zinc-800 rounded-lg py-2.5">Bientôt disponible</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comptant */}
        <section data-testid="tarifs-comptant">
          <h2 className="text-lg font-bold text-white mb-3">Tarif comptant</h2>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Durée</th>
                  {freqs.map((f) => <th key={f} className="text-center px-4 py-3">{FREQ_LABEL[f] || f}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {durations.map((dur) => (
                  <tr key={dur}>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">{dur} sem.</td>
                    {freqs.map((f) => (
                      <td key={f} className="px-4 py-3 text-center text-zinc-200">{money(cell(dur, f))}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Prélèvement échelonné */}
        <section data-testid="tarifs-prelevement">
          <h2 className="text-lg font-bold text-amber-300 mb-1">Prélèvement échelonné (+{surcharge.toFixed(0)} %)</h2>
          <p className="text-xs text-zinc-500 mb-3">Disponible dès {minW} semaines · 1 prélèvement toutes les 4 semaines.</p>
          <div className="overflow-x-auto rounded-xl border border-amber-500/20">
            <table className="w-full text-sm">
              <thead className="bg-amber-950/40 text-amber-200/80 text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Durée</th>
                  {freqs.map((f) => <th key={f} className="text-center px-4 py-3">{FREQ_LABEL[f] || f}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {durations.filter((d) => d >= minW).map((dur) => {
                  const n = Math.floor(dur / 4);
                  return (
                    <tr key={dur}>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">{dur} sem. <span className="text-[10px] text-zinc-500">({n}×)</span></td>
                      {freqs.map((f) => {
                        const reg = Number(cell(dur, f)) || 0;
                        if (!reg) return <td key={f} className="px-4 py-3 text-center text-zinc-600">—</td>;
                        const total = reg * mult;
                        return (
                          <td key={f} className="px-4 py-3 text-center">
                            <div className="text-zinc-100 font-medium">{money(total)}</div>
                            <div className="text-[11px] text-emerald-400/80">{n}× {money(total / n)}</div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <p className="text-[11px] text-zinc-600 text-center pt-4">
          Val-d'Or, QC · {cfg.phone} · {cfg.website} — Tarifs indicatifs, sujets à changement sans préavis.
        </p>
      </div>
      <PublicContactSection source="tarifs" title="Une question sur les tarifs ?" subtitle="Écris-nous, on t'aide à choisir le forfait qui te convient." />
    </div>
  );
}
